import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import Recaptcha from '@components/recaptcha'

const CREATE_SURVEY_RESPONSE = gql`
  mutation CreateSurveyResponse($surveyName: String!, $data: JSON!) {
    createSurveyResponse(surveyName: $surveyName, data: $data) {
      success
    }
  }
`

const interestOptions = [
  'True beginner',
  'Golf swing mechanics',
  'Social',
  'Competition',
]

const validationSchema = Yup.object().shape({
  zipCode: Yup.string().max(10).required('This field is required'),
  email: Yup.string().email().required('This field is required'),
  interest: Yup.string().oneOf(interestOptions),
})

const GroupEventsInterestForm = ({ displayEventInterests }) => {
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [isFormSubmitSuccess, setIsFormSubmitSuccess] = useState(null)
  const {
    handleSubmit, register, control, errors, formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
  })
  const [createSurveyResponse] = useMutation(CREATE_SURVEY_RESPONSE, {
    context: { clientName: 'coach-tools', recaptchaToken },
    onCompleted: (data) => {
      setIsFormSubmitSuccess(data.createSurveyResponse.success)
    },
    onError: () => {
      setIsFormSubmitSuccess(false)
    },
  })
  const onSubmit = (values) => {
    setIsFormSubmitSuccess(null)
    return createSurveyResponse({
      variables: {
        surveyName: 'group-events-interest',
        data: {
          zipCode: values.zipCode,
          email: values.email,
          interest: values.interest,
        },
      },
    })
  }

  if (isFormSubmitSuccess === true) {
    return (
      <Box textAlign="center">
        <Box mb={1}>
          <Typography color="textPrimary" variant="h6">You&apos;re on the list!</Typography>
        </Box>
        <Box color="text.secondary">
          <Box mb={3}>
            <Typography variant="body1" style={{ lineHeight: 1.5 }}>
              Thank you so much for your interest. We&apos;ll let you know when events are
              available near you.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="body1" style={{ lineHeight: 1.5 }}>
              In the meantime, you can find a PGA Coach in your area by clicking below.
            </Typography>
          </Box>
        </Box>
        <Link href="/coach" passHref>
          <Button variant="contained" color="secondary" size="small">Find a coach</Button>
        </Link>
      </Box>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mb={2}>
        <TextField
          name="zipCode"
          inputRef={register}
          error={!!errors?.zipCode}
          helperText={errors?.zipCode?.message}
          label="Zip"
          variant="outlined"
          fullWidth
          inputProps={{
            style: { background: 'white' },
          }}
        />
      </Box>
      <Box mb={2}>
        <TextField
          name="email"
          inputRef={register}
          error={!!errors?.email}
          helperText={errors?.email?.message}
          label="Email"
          variant="outlined"
          fullWidth
          inputProps={{
            style: { background: 'white' },
          }}
        />
      </Box>
      {displayEventInterests && (
        <Box mb={2}>
          <FormControl
            error={!!errors.interest}
            variant="outlined"
            fullWidth
          >
            <InputLabel>Event interest</InputLabel>
            <Controller
              name="interest"
              control={control}
              defaultValue=""
              as={(
                <Select
                  label="Event interest"
                  labelWidth={98}
                >
                  {interestOptions.map(val => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
          {errors.interest ? (
            <Typography variant="caption" color="error">
            Please select an option
            </Typography>
          ) : null}
        </Box>
      )}
      <Recaptcha setToken={setRecaptchaToken} />
      <Button
        type="submit"
        variant="contained"
        size="large"
        color="secondary"
        fullWidth
        disabled={isSubmitting}
      >
        Sign up
      </Button>
      {isFormSubmitSuccess === false && (
        <Typography variant="caption" color="error">
          An error has occurred. Please try again later.
        </Typography>
      )}
    </form>
  )
}

GroupEventsInterestForm.propTypes = {
  displayEventInterests: PropTypes.bool,
}
export default GroupEventsInterestForm
